import { CATEGORIES, parseArticle, parseIndexOrder, parseTips, plainText, type HelpArticle, type Tip } from "./parse";

/**
 * The help content (ALN-53), bundled from docs/help at build time. docs/help is the source of
 * truth; nothing here is hand-copied.
 */
const files = import.meta.glob("../../../../docs/help/*.md", { query: "?raw", import: "default", eager: true }) as Record<string, string>;
const byName = Object.fromEntries(Object.entries(files).map(([p, text]) => [p.split("/").pop()!, text]));

const order = parseIndexOrder(byName["README.md"] ?? "");
const rank = (id: string) => { const i = order.indexOf(id); return i < 0 ? order.length : i; };

export const ARTICLES: HelpArticle[] = Object.entries(byName)
  .filter(([n]) => n !== "README.md" && n !== "tips.md")
  .map(([n, text]) => parseArticle(n, text))
  .sort((a, b) => rank(a.id) - rank(b.id) || a.title.localeCompare(b.title));

export const ARTICLE_BY_ID: Record<string, HelpArticle> = Object.fromEntries(ARTICLES.map(a => [a.id, a]));

export const TIPS: Record<string, Tip> = Object.fromEntries(parseTips(byName["tips.md"] ?? "").map(t => [t.label, t]));

export const categoryLabel = (key: string) => CATEGORIES.find(([k]) => k === key)?.[1] ?? key;

const HAY = new Map(ARTICLES.map(a => [a.id, plainText(a).toLowerCase()]));

/** Articles whose title, lead or body contain every word of the query. Title matches first. */
export function searchHelp(q: string): HelpArticle[] {
  const terms = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (!terms.length) return ARTICLES;
  return ARTICLES
    .filter(a => terms.every(t => HAY.get(a.id)!.includes(t)))
    .map(a => ({ a, s: a.title.toLowerCase().includes(terms.join(" ")) ? 0 : a.lead.toLowerCase().includes(terms.join(" ")) ? 1 : 2 }))
    .sort((x, y) => x.s - y.s)
    .map(x => x.a);
}

/** The article for a page: the one whose route is the path's first segment, else Getting started. */
export function articleForPath(path: string): HelpArticle {
  const seg = path.replace(/^\//, "").split(/[/?#]/)[0] ?? "";
  if (seg === "help") {
    const id = path.split("/")[2];
    if (id && ARTICLE_BY_ID[id]) return ARTICLE_BY_ID[id];
  }
  return ARTICLES.find(a => a.route === seg) ?? ARTICLE_BY_ID["getting-started"] ?? ARTICLES[0]!;
}
